import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import {
  FALLBACK_STYLE_OPTIONS,
  normalizePreferredStyles,
} from '../../../utils/preferredStyles';
import AnchoredPortalContext from '../AnchoredPortal/AnchoredPortalContext';
import {
  formatStyleTagForDisplay,
  normalizeStyleTag,
  normalizeStyleTagKey,
  normalizeStyleTagList,
  styleTagsSignature,
} from '../../../utils/styleTags';
import { userProfileService } from '../../../services/userProfileService';
import './index.scss';

const MAX_VISIBLE_OPTIONS = 80;
const MENU_GAP = 10;
const VIEWPORT_PADDING = 12;

const DEFAULT_REMOVE_LABEL_TOKEN = 'style';

const RemoveIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

function getPortalRoot() {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.getElementById('portal') || document.body;
}

const replaceLabelToken = (template, token, value) => (
  String(template || '')
    .split(`{{${token}}}`)
    .join(value)
);

const formatRemoveLabel = (template, token, value) => {
  const labelTemplate = String(template || '');
  const resolvedToken = token || DEFAULT_REMOVE_LABEL_TOKEN;
  if (labelTemplate.includes(`{{${resolvedToken}}}`)) {
    return replaceLabelToken(labelTemplate, resolvedToken, value);
  }
  if (labelTemplate.includes('{{style}}')) {
    return replaceLabelToken(labelTemplate, DEFAULT_REMOVE_LABEL_TOKEN, value);
  }
  return `${labelTemplate}: ${value}`;
};

const StyleTagsEditor = forwardRef(function StyleTagsEditor({
  values = [],
  onSave,
  options = FALLBACK_STYLE_OPTIONS,
  loadOptions = userProfileService.listPreferredStyles,
  normalizeOptions = normalizePreferredStyles,
  placeholder = '',
  ariaLabel = 'Styles',
  removeLabel = 'Remove {{style}}',
  removeLabelToken = DEFAULT_REMOVE_LABEL_TOKEN,
  customOptionLabel = 'Use custom style',
  emptyLabel = 'No matching styles',
  className = '',
  maxLength = 300,
  maxItems = null,
  menuAnchor = 'input',
}, ref) {
  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const unregisterOwnedNodeRef = useRef(null);
  const isMountedRef = useRef(false);
  const styleCatalogLoadRequestedRef = useRef(false);
  const saveRequestRef = useRef(0);
  const latestRequestedSignatureRef = useRef(styleTagsSignature(values));
  const pendingSaveCountRef = useRef(0);
  const deferredControlledTagsRef = useRef(null);
  const lastPropSignatureRef = useRef(styleTagsSignature(values));
  const submittedRequestIdsBySignatureRef = useRef(new Map());
  const saveStatusByRequestIdRef = useRef(new Map());
  const rejectedSignatureRef = useRef(null);
  const retryInteractionRef = useRef(0);
  const consumedRejectedSignatureRef = useRef(null);
  const suppressBlurSaveRef = useRef(false);
  const blurSaveTimeoutRef = useRef(null);
  const blurSaveRequestRef = useRef(0);
  const draftRef = useRef('');
  const draftRevisionRef = useRef(0);

  const propTags = useMemo(() => normalizeStyleTagList(values), [values]);
  const propSignature = useMemo(() => styleTagsSignature(propTags), [propTags]);
  const confirmedTagsRef = useRef(propTags);
  const confirmedSignatureRef = useRef(propSignature);
  const confirmedRequestIdRef = useRef(0);
  const [optimisticTags, setOptimisticTags] = useState(propTags);
  const selectedTags = optimisticTags;
  const selectedSignature = useMemo(() => styleTagsSignature(selectedTags), [selectedTags]);
  const selectedKeys = useMemo(
    () => new Set(selectedTags.map(normalizeStyleTagKey)),
    [selectedTags],
  );
  const [draft, setDraftState] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0, width: 0 });
  const fallbackOptions = useMemo(
    () => normalizeOptions(options),
    [normalizeOptions, options],
  );
  const [styleOptions, setStyleOptions] = useState(fallbackOptions);
  const anchoredPortalContext = useContext(AnchoredPortalContext);
  const maxSelectedItems = Number.isInteger(maxItems) && maxItems > 0 ? maxItems : null;
  const isAtItemLimit = maxSelectedItems !== null && selectedTags.length >= maxSelectedItems;

  const setDraft = useCallback((nextValue) => {
    const resolvedValue = typeof nextValue === 'function'
      ? nextValue(draftRef.current)
      : nextValue;
    draftRef.current = resolvedValue;
    draftRevisionRef.current += 1;
    setDraftState(resolvedValue);
    return draftRevisionRef.current;
  }, []);

  const applyControlledTags = useCallback((nextTags, nextSignature) => {
    if (rejectedSignatureRef.current === nextSignature) {
      rejectedSignatureRef.current = null;
    }
    if (consumedRejectedSignatureRef.current?.signature === nextSignature) {
      consumedRejectedSignatureRef.current = null;
    }
    confirmedTagsRef.current = nextTags;
    confirmedSignatureRef.current = nextSignature;
    confirmedRequestIdRef.current = saveRequestRef.current;
    latestRequestedSignatureRef.current = nextSignature;
    deferredControlledTagsRef.current = null;
    setOptimisticTags((currentTags) => (
      styleTagsSignature(currentTags) === nextSignature ? currentTags : nextTags
    ));
  }, []);

  const deferControlledTags = useCallback((nextTags, nextSignature) => {
    const submittedRequestIds = submittedRequestIdsBySignatureRef.current.get(nextSignature);
    deferredControlledTagsRef.current = {
      tags: nextTags,
      signature: nextSignature,
      sourceRequestId: submittedRequestIds?.[0] || null,
    };
  }, []);

  const applyConfirmedTags = useCallback(() => {
    const confirmedTags = confirmedTagsRef.current;
    const confirmedSignature = confirmedSignatureRef.current;
    latestRequestedSignatureRef.current = confirmedSignature;
    setOptimisticTags((currentTags) => (
      styleTagsSignature(currentTags) === confirmedSignature ? currentTags : confirmedTags
    ));
  }, []);

  const settlePendingSaves = useCallback(() => {
    const deferredControlledTags = deferredControlledTagsRef.current;
    if (deferredControlledTags) {
      const { tags, signature, sourceRequestId } = deferredControlledTags;
      const sourceStatus = sourceRequestId === null
        ? null
        : saveStatusByRequestIdRef.current.get(sourceRequestId);
      const isStaleRequestEcho = sourceRequestId !== null
        && (sourceStatus !== 'fulfilled' || confirmedRequestIdRef.current > sourceRequestId);

      deferredControlledTagsRef.current = null;
      if (isStaleRequestEcho) {
        applyConfirmedTags();
      } else {
        applyControlledTags(tags, signature);
      }
    } else {
      applyConfirmedTags();
    }
    submittedRequestIdsBySignatureRef.current.clear();
    saveStatusByRequestIdRef.current.clear();
  }, [applyConfirmedTags, applyControlledTags]);

  const setMenuRef = useCallback((node) => {
    if (unregisterOwnedNodeRef.current) {
      unregisterOwnedNodeRef.current();
      unregisterOwnedNodeRef.current = null;
    }

    menuRef.current = node;

    if (node && anchoredPortalContext?.registerOwnedNode) {
      unregisterOwnedNodeRef.current = anchoredPortalContext.registerOwnedNode(node);
    }
  }, [anchoredPortalContext]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (unregisterOwnedNodeRef.current) {
        unregisterOwnedNodeRef.current();
        unregisterOwnedNodeRef.current = null;
      }
      if (blurSaveTimeoutRef.current) {
        clearTimeout(blurSaveTimeoutRef.current);
        blurSaveTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    styleCatalogLoadRequestedRef.current = false;
    setStyleOptions(fallbackOptions);
  }, [fallbackOptions, loadOptions]);

  useEffect(() => {
    const propSignatureChanged = propSignature !== lastPropSignatureRef.current;
    lastPropSignatureRef.current = propSignature;
    if (pendingSaveCountRef.current > 0) {
      if (propSignatureChanged || deferredControlledTagsRef.current?.signature === propSignature) {
        deferControlledTags(propTags, propSignature);
      }
      return;
    }
    applyControlledTags(propTags, propSignature);
  }, [applyControlledTags, deferControlledTags, propSignature, propTags]);

  useEffect(() => {
    if (!isOpen || styleCatalogLoadRequestedRef.current || typeof loadOptions !== 'function') {
      return undefined;
    }
    styleCatalogLoadRequestedRef.current = true;
    Promise.resolve(loadOptions())
      .then((data) => {
        if (!isMountedRef.current) return;
        const normalized = normalizeOptions(data);
        setStyleOptions(normalized.length > 0 ? normalized : fallbackOptions);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setStyleOptions(fallbackOptions);
      });
    return undefined;
  }, [fallbackOptions, isOpen, loadOptions, normalizeOptions]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (event) => {
      const insideTrigger = containerRef.current?.contains(event.target);
      const insideMenu = menuRef.current?.contains(event.target);
      if (insideTrigger || insideMenu) return;
      setIsOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        suppressBlurSaveRef.current = true;
        setDraft('');
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, setDraft]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const compute = () => {
      const trigger = menuAnchor === 'control'
        ? controlRef.current
        : inputRef.current;
      if (!trigger || typeof window === 'undefined') return;
      const tRect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const desiredWidth = Math.max(tRect.width, 260);
      const menuHeight = menuRef.current?.getBoundingClientRect?.().height || 280;

      let top = tRect.bottom + MENU_GAP;
      let left = tRect.left;
      if (top + menuHeight + VIEWPORT_PADDING > viewportHeight) {
        const flippedTop = tRect.top - MENU_GAP - menuHeight;
        top = flippedTop >= VIEWPORT_PADDING
          ? flippedTop
          : Math.max(VIEWPORT_PADDING, viewportHeight - menuHeight - VIEWPORT_PADDING);
      }
      const maxLeft = viewportWidth - desiredWidth - VIEWPORT_PADDING;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
      setMenuCoords({ top, left, width: desiredWidth });
    };

    compute();
    const handleScroll = () => compute();
    const handleResize = () => compute();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [draft, isOpen, menuAnchor, selectedTags.length]);

  const normalizedDraft = normalizeStyleTagKey(draft);
  const filteredOptions = useMemo(() => {
    if (isAtItemLimit) return [];
    const list = Array.isArray(styleOptions) ? styleOptions : [];
    return list
      .filter((option) => {
        const labelKey = normalizeStyleTagKey(option?.label);
        if (!labelKey || selectedKeys.has(labelKey)) return false;
        if (!normalizedDraft) return true;
        const labelMatch = labelKey.includes(normalizedDraft);
        const aliasMatch = Array.isArray(option?.aliases)
          && option.aliases.some((alias) => normalizeStyleTagKey(alias).includes(normalizedDraft));
        return labelMatch || aliasMatch;
      })
      .slice(0, MAX_VISIBLE_OPTIONS);
  }, [isAtItemLimit, normalizedDraft, selectedKeys, styleOptions]);

  const showCustomOption = !isAtItemLimit && Boolean(normalizedDraft) && !selectedKeys.has(normalizedDraft)
    && !filteredOptions.some((option) => normalizeStyleTagKey(option?.label) === normalizedDraft);

  const commitTags = useCallback((nextValues, {
    clearDraftOnSuccess = false,
    closeOnSuccess = false,
    restoreDraftOnFailure = '',
  } = {}) => {
    const nextTags = normalizeStyleTagList(nextValues, { splitCommas: true })
      .slice(0, maxSelectedItems || undefined);
    const nextSignature = styleTagsSignature(nextTags);
    const rejectedSignature = rejectedSignatureRef.current;
    const consumedRejectedSignature = consumedRejectedSignatureRef.current;
    const retryAlreadyConsumed = rejectedSignature === nextSignature
      && consumedRejectedSignature?.signature === nextSignature
      && consumedRejectedSignature?.interaction === retryInteractionRef.current;
    if (retryAlreadyConsumed) {
      if (closeOnSuccess) setIsOpen(false);
      return Promise.resolve(nextTags);
    }
    if (nextSignature === selectedSignature && rejectedSignature !== nextSignature) {
      if (clearDraftOnSuccess) setDraft('');
      if (closeOnSuccess) setIsOpen(false);
      return Promise.resolve(nextTags);
    }

    if (rejectedSignature === nextSignature) {
      rejectedSignatureRef.current = null;
      consumedRejectedSignatureRef.current = {
        signature: nextSignature,
        interaction: retryInteractionRef.current,
      };
    } else if (consumedRejectedSignatureRef.current) {
      consumedRejectedSignatureRef.current = null;
    }
    const requestId = saveRequestRef.current + 1;
    saveRequestRef.current = requestId;
    latestRequestedSignatureRef.current = nextSignature;
    submittedRequestIdsBySignatureRef.current.set(nextSignature, [
      ...(submittedRequestIdsBySignatureRef.current.get(nextSignature) || []),
      requestId,
    ]);
    saveStatusByRequestIdRef.current.delete(requestId);
    pendingSaveCountRef.current += 1;
    setOptimisticTags(nextTags);
    const clearedDraftRevision = clearDraftOnSuccess ? setDraft('') : null;
    if (closeOnSuccess) setIsOpen(false);

    return Promise.resolve(onSave?.(nextTags))
      .then(() => {
        if (rejectedSignatureRef.current === nextSignature) {
          rejectedSignatureRef.current = null;
        }
        if (consumedRejectedSignatureRef.current?.signature === nextSignature) {
          consumedRejectedSignatureRef.current = null;
        }
        saveStatusByRequestIdRef.current.set(requestId, 'fulfilled');
        if (requestId > confirmedRequestIdRef.current) {
          confirmedTagsRef.current = nextTags;
          confirmedSignatureRef.current = nextSignature;
          confirmedRequestIdRef.current = requestId;
        }
        if (saveRequestRef.current !== requestId) return;
        return nextTags;
      })
      .catch((err) => {
        rejectedSignatureRef.current = nextSignature;
        saveStatusByRequestIdRef.current.set(requestId, 'rejected');
        console.error('StyleTagsEditor save failed', err);
        const confirmedTags = confirmedTagsRef.current;
        if (saveRequestRef.current === requestId && isMountedRef.current) {
          latestRequestedSignatureRef.current = confirmedSignatureRef.current;
          setOptimisticTags(confirmedTags);
          if (
            restoreDraftOnFailure
            && clearedDraftRevision !== null
            && draftRevisionRef.current === clearedDraftRevision
            && draftRef.current === ''
          ) {
            setDraft(restoreDraftOnFailure);
          }
        }
        return confirmedTags;
      })
      .finally(() => {
        pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current - 1);
        if (pendingSaveCountRef.current === 0 && isMountedRef.current) {
          settlePendingSaves();
        }
      });
  }, [maxSelectedItems, onSave, selectedSignature, setDraft, settlePendingSaves]);

  const commitDraft = useCallback((options = {}) => {
    const parsedDraft = normalizeStyleTagList(draft, { splitCommas: true });
    if (parsedDraft.length === 0) {
      if (options.closeOnSuccess) setIsOpen(false);
      return Promise.resolve(selectedTags);
    }
    const submittedDraft = draft;
    return commitTags([...selectedTags, ...parsedDraft], {
      clearDraftOnSuccess: true,
      closeOnSuccess: options.closeOnSuccess,
      restoreDraftOnFailure: submittedDraft,
    });
  }, [commitTags, draft, selectedTags]);

  const cancelPendingBlurSave = useCallback(() => {
    blurSaveRequestRef.current += 1;
    if (blurSaveTimeoutRef.current) {
      clearTimeout(blurSaveTimeoutRef.current);
      blurSaveTimeoutRef.current = null;
    }
  }, []);

  const handleSelectOption = useCallback((label) => {
    retryInteractionRef.current += 1;
    cancelPendingBlurSave();
    const nextTag = normalizeStyleTag(label);
    if (!nextTag || selectedKeys.has(normalizeStyleTagKey(nextTag)) || isAtItemLimit) {
      setDraft('');
      setIsOpen(true);
      inputRef.current?.focus();
      return;
    }
    setIsOpen(true);
    commitTags([...selectedTags, nextTag], {
      clearDraftOnSuccess: true,
    }).finally(() => {
      if (!isMountedRef.current) return;
      setIsOpen(true);
      inputRef.current?.focus();
    });
  }, [cancelPendingBlurSave, commitTags, isAtItemLimit, selectedKeys, selectedTags, setDraft]);

  const handleRemoveTag = useCallback((tag) => {
    retryInteractionRef.current += 1;
    const target = normalizeStyleTagKey(tag);
    const nextTags = selectedTags.filter((item) => normalizeStyleTagKey(item) !== target);
    commitTags(nextTags);
  }, [commitTags, selectedTags]);

  const handleChange = useCallback((event) => {
    retryInteractionRef.current += 1;
    setDraft(event.target.value);
    if (!isOpen) setIsOpen(true);
  }, [isOpen, setDraft]);

  const handleFocus = useCallback(() => {
    retryInteractionRef.current += 1;
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    if (suppressBlurSaveRef.current) {
      suppressBlurSaveRef.current = false;
      return;
    }
    const requestId = blurSaveRequestRef.current + 1;
    blurSaveRequestRef.current = requestId;
    if (blurSaveTimeoutRef.current) {
      clearTimeout(blurSaveTimeoutRef.current);
    }
    blurSaveTimeoutRef.current = setTimeout(() => {
      blurSaveTimeoutRef.current = null;
      if (blurSaveRequestRef.current !== requestId) return;
      const active = document.activeElement;
      if (containerRef.current?.contains(active) || menuRef.current?.contains(active)) return;
      commitDraft({ closeOnSuccess: true });
    }, 80);
  }, [commitDraft]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      cancelPendingBlurSave();
      suppressBlurSaveRef.current = true;
      commitDraft({ closeOnSuccess: true }).finally(() => inputRef.current?.blur());
    }
  }, [cancelPendingBlurSave, commitDraft]);

  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      inputRef.current?.focus();
    },
    flushDraft: () => {
      cancelPendingBlurSave();
      return commitDraft();
    },
  }), [cancelPendingBlurSave, commitDraft]);

  const portalRoot = getPortalRoot();

  return (
    <div ref={containerRef} className={classNames('style-tags-editor', className)}>
      <div className="style-tags-editor__control" ref={controlRef}>
        {selectedTags.length > 0 ? (
          <ul className="style-tags-editor__chips" aria-label={ariaLabel}>
            {selectedTags.map((tag) => (
              <li className="style-tags-editor__chip" key={normalizeStyleTagKey(tag)}>
                <span>{formatStyleTagForDisplay(tag)}</span>
                <button
                  type="button"
                  className="style-tags-editor__remove"
                  aria-label={formatRemoveLabel(removeLabel, removeLabelToken, tag)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleRemoveTag(tag)}
                >
                  <RemoveIcon />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <input
          ref={inputRef}
          type="text"
          className="style-tags-editor__input"
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoComplete="off"
          maxLength={maxLength}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>

      {isOpen && portalRoot ? ReactDOM.createPortal(
        <div
          ref={setMenuRef}
          className="style-tags-editor__menu"
          role="listbox"
          style={{
            top: menuCoords.top,
            left: menuCoords.left,
            width: menuCoords.width || undefined,
          }}
        >
          {filteredOptions.length === 0 && !showCustomOption ? (
            <div className="style-tags-editor__empty">{emptyLabel}</div>
          ) : null}
          {filteredOptions.map((option) => (
            <button
              key={option.id || option.label}
              type="button"
              role="option"
              aria-selected={false}
              className="style-tags-editor__option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelectOption(option.label)}
            >
              <span className="style-tags-editor__option-label">{option.label}</span>
            </button>
          ))}
          {showCustomOption ? (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="style-tags-editor__option style-tags-editor__option--custom"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelectOption(draft.trim())}
            >
              <span className="style-tags-editor__option-label">{draft.trim()}</span>
              <span className="style-tags-editor__option-sub">{customOptionLabel}</span>
            </button>
          ) : null}
        </div>,
        portalRoot,
      ) : null}
    </div>
  );
});

export default StyleTagsEditor;

StyleTagsEditor.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string),
  onSave: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    aliases: PropTypes.arrayOf(PropTypes.string),
  })),
  loadOptions: PropTypes.func,
  normalizeOptions: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  removeLabel: PropTypes.string,
  removeLabelToken: PropTypes.string,
  customOptionLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  className: PropTypes.string,
  maxLength: PropTypes.number,
  maxItems: PropTypes.number,
  menuAnchor: PropTypes.oneOf(['control', 'input']),
};
